import moment, { Moment } from 'moment';
import { formatDate } from 'src/utils/format';

export type DateLike = Date | string | Moment;

export interface WorkWeek {
  weekNumber: number; // 1-based, aligned with backend DateUtilities
  year: number; // year of the week-ending Friday
}

/** Firetrol work-week number + year for a date. Work week runs Sat -> Fri. @registry Canonical work-week (Sat-Fri) calculator; mirrors backend DateUtilities. */
export const getWorkWeek = (date: DateLike): WorkWeek => {
  const m = moment(date);
  // Friday that ends this work week.
  // Saturday (day 6) is the *start* of a work week, so its ending Friday is +6 days.
  // For Sun-Fri, the ending Friday is this week's Friday (moment day 5 with Sun-based week).
  const friday = m.day() === 6 ? m.clone().add(6, 'days') : m.clone().day(5);
  const year = friday.year();
  const jan1 = moment([year, 0, 1]);
  const daysUntilFirstFriday = (5 - jan1.day() + 7) % 7;
  const firstFriday = jan1.clone().add(daysUntilFirstFriday, 'days');
  const weekNumber = Math.floor(friday.diff(firstFriday, 'days') / 7) + 1;
  return { weekNumber, year };
};

/** Just the work-week number. */
export const getWorkWeekNumber = (date: DateLike): number => {
  return getWorkWeek(date).weekNumber;
};

/** Year of the work week (the year of its week-ending Friday). */
export const getWorkWeekYear = (date: DateLike): number => {
  return getWorkWeek(date).year;
};

/** Current work week at call time. */
export const getCurrentWorkWeek = (): WorkWeek => {
  return getWorkWeek(new Date());
};

/** Mirrors backend DateUtilities.GetWeekEndingFriday exactly. Returns a Date at local midnight. @registry Week-number+year to week-ending-Friday Date; the inverse building block behind most work-week range helpers. */
export const getWeekEndingFriday = (weekNumber: number, year: number): Date => {
  const jan1 = moment([year, 0, 1]);
  const daysUntilFirstFriday = (5 - jan1.day() + 7) % 7;
  return jan1
    .add(daysUntilFirstFriday + (weekNumber - 1) * 7, 'days')
    .startOf('day')
    .toDate();
};

/** Saturday that starts the given work week. */
export const getWorkWeekStart = (weekNumber: number, year: number): Date => {
  return moment(getWeekEndingFriday(weekNumber, year))
    .subtract(6, 'days')
    .startOf('day')
    .toDate();
};

/** { start: Sat 00:00 local, end: Fri 23:59:59.999 local } for a work week. */
export const getWorkWeekRange = (
  weekNumber: number,
  year: number,
): { start: Date; end: Date } => {
  const start = getWorkWeekStart(weekNumber, year);
  const end = moment(getWeekEndingFriday(weekNumber, year))
    .endOf('day')
    .toDate();
  return { start, end };
};

/** "Apr 11 - Apr 17" (Sat - Fri). @registry Human-readable work-week date-range label for timesheet/week selectors. */
export const formatWorkWeekRange = (
  weekNumber: number,
  year: number,
): string => {
  return `${formatDate(
    getWorkWeekStart(weekNumber, year),
    'MMM DD',
  )} - ${formatDate(getWeekEndingFriday(weekNumber, year), 'MMM DD')}`;
};

/** "Apr 17, 2026" — mirrors backend DateUtilities.FormatWeekEnding. */
export const formatWeekEnding = (weekNumber: number, year: number): string => {
  return formatDate(getWeekEndingFriday(weekNumber, year), 'MMM D, YYYY');
};

/** Composite id ("2026-14") used by week-selector options so history is browsable across years. @registry Encodes a year+weekNumber pair into the composite id used across week-selector components; pair with parseWorkWeekId. */
export const formatWorkWeekId = (year: number, weekNumber: number): string =>
  `${year}-${weekNumber}`;

/** Inverse of `formatWorkWeekId`. */
export const parseWorkWeekId = (
  id: string,
): { year: number; weekNumber: number } => {
  const [year, weekNumber] = id.split('-').map(Number);
  return { year, weekNumber };
};

/** Inclusive count of distinct work weeks between two dates. @registry Counts distinct Sat-Fri work weeks overlapping a date range; used for week-count summaries in Time/Dailies. */
export const getWorkWeekCount = (start: DateLike, end: DateLike): number => {
  const startWeek = getWorkWeek(start);
  const endDate = moment(end);
  let count = 0;
  const cursor = moment(getWorkWeekStart(startWeek.weekNumber, startWeek.year));
  // Advance in 7-day steps, counting each work week that overlaps [start, end]
  while (cursor.isSameOrBefore(endDate, 'day')) {
    count++;
    cursor.add(7, 'days');
  }
  return count;
};
